import type {
  MRT_Column,
  MRT_ColumnFiltersState,
  MRT_RowData,
  MRT_TableInstance
} from "mantine-react-table-open";
import { parseFromValuesOrFunc } from "mantine-react-table-open";
import { getFilterChips } from "../../mrt/utils/getFilterChips";
import { FilterPillsGroup } from "./FilterPillsGroup";

interface MRTActiveFiltersRowProps<TData extends MRT_RowData> {
  table: MRT_TableInstance<TData>;
}

/** Row of removable chips for all active column filters */
function MRTActiveFiltersRow<TData extends MRT_RowData>({
  table
}: MRTActiveFiltersRowProps<TData>) {
  const { columnFilters } = table.getState();

  const pills = columnFilters.flatMap((columnFilter) => {
    const column = table.getColumn(columnFilter.id);
    if (!column || !column.getCanFilter()) return [];

    return getFilterChips({
      columnId: columnFilter.id,
      filterValue: columnFilter.value,
      filterVariant: column.columnDef.filterVariant,
      getOptionLabel: getFilterOptionLabelResolver(column, table),
      header: column.columnDef.header
    });
  });

  if (pills.length === 0) return null;

  return (
    <FilterPillsGroup
      pills={pills}
      onRemove={(columnId, nextFilterValue) =>
        table.getColumn(columnId)?.setFilterValue(nextFilterValue)
      }
      onRemoveAll={() => clearAllLeafColumnFilters(table)}
    />
  );
}

/** Clears every leaf column filter, avoiding MRT's handleClear which feeds null/undefined into inputs. */
function clearAllLeafColumnFilters<TData extends MRT_RowData>(
  table: MRT_TableInstance<TData>
) {
  const newFilters = table
    .getAllLeafColumns()
    .reduce<MRT_ColumnFiltersState>((acc, column) => {
      if (column.getFilterValue() === undefined || !column.getCanFilter())
        return acc;

      const variant = column.columnDef.filterVariant;
      if (variant === "select") {
        return [...acc, { id: column.id, value: "" }];
      } else if (variant === "range" || variant === "date-range") {
        return [...acc, { id: column.id, value: ["", ""] }];
      } else {
        return [...acc, { id: column.id, value: undefined }];
      }
    }, []);

  table.setColumnFilters(() => newFilters);
}

/** Resolves select filter values to the labels defined in the column's filter options */
function getFilterOptionLabelResolver<TData extends MRT_RowData>(
  column: MRT_Column<TData>,
  table: MRT_TableInstance<TData>
) {
  const { filterVariant } = column.columnDef;
  if (filterVariant !== "select" && filterVariant !== "multi-select") {
    return undefined;
  }

  const filterInputProps =
    filterVariant === "select"
      ? (column.columnDef.mantineFilterSelectProps ??
        table.options.mantineFilterSelectProps)
      : (column.columnDef.mantineFilterMultiSelectProps ??
        table.options.mantineFilterMultiSelectProps);

  const optionsData = parseFromValuesOrFunc(filterInputProps, {
    column,
    table
  })?.data;

  if (!optionsData) return undefined;

  return (value: unknown) => findComboboxItemLabel(optionsData, value);
}

function findComboboxItemLabel(
  optionsData: ReadonlyArray<unknown>,
  value: unknown
): string | undefined {
  for (const item of optionsData) {
    if (typeof item === "string") {
      if (item === value) return item;
      continue;
    }

    if (!item || typeof item !== "object") continue;

    if ("items" in item && Array.isArray(item.items)) {
      const groupItemLabel = findComboboxItemLabel(item.items, value);
      if (groupItemLabel !== undefined) return groupItemLabel;
      continue;
    }

    if ("value" in item && item.value === value) {
      return "label" in item ? String(item.label) : String(item.value);
    }
  }

  return undefined;
}

export { MRTActiveFiltersRow };
